import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { wishlistAPI, cartAPI, authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, ShoppingCart, Trash2, Star } from 'lucide-react';
import Layout from '@/components/Layout';
import { toast } from 'sonner';

const WishlistPage = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthAndLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    try {
      await authAPI.getMe();
      setIsAuthenticated(true);
      loadWishlist();
    } catch (error) {
      setIsAuthenticated(false);
      setLoading(false);
    }
  };

  const loadWishlist = async () => {
    try {
      setLoading(true);
      const data = await wishlistAPI.getWishlist();
      setWishlistItems(data);
    } catch (error) {
      console.error('Error loading wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      await wishlistAPI.removeFromWishlist(productId);
      setWishlistItems(wishlistItems.filter(item => item.product_id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    }
  };

  const handleAddToCart = async (productId) => {
    try {
      await cartAPI.addToCart(productId, 1);
      toast.success('Added to cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const handleMoveToCart = async (productId) => {
    try {
      await cartAPI.addToCart(productId, 1);
      await wishlistAPI.removeFromWishlist(productId);
      setWishlistItems(wishlistItems.filter(item => item.product_id !== productId));
      toast.success('Moved to cart');
    } catch (error) {
      toast.error('Failed to move to cart');
    }
  };

  if (!isAuthenticated && !loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom">
            <div className="text-center py-16">
              <Heart size={64} className="mx-auto mb-6 text-muted-foreground" />
              <h1 className="text-3xl font-bold mb-4">Your Wishlist</h1>
              <p className="text-muted-foreground mb-8">Please sign in to view your wishlist</p>
              <Button onClick={() => navigate('/login')} className="btn-primary">
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="section-padding" data-testid="wishlist-page">
        <div className="container-custom">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">My Wishlist</h1>
            <p className="text-muted-foreground text-lg">
              {wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'} saved
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton h-64 sm:h-96 rounded-2xl"></div>
              ))}
            </div>
          ) : wishlistItems.length === 0 ? (
            <div className="text-center py-16 bg-secondary/20 rounded-2xl">
              <Heart size={64} className="mx-auto mb-6 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-4">Your wishlist is empty</h2>
              <p className="text-muted-foreground mb-8">Start adding products you love!</p>
              <Button onClick={() => navigate('/products')} className="btn-primary">
                Browse Products
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
              {wishlistItems.map((item) => (
                <Card
                  key={item.wishlist_id}
                  className="card-product group"
                  data-testid={`wishlist-item-${item.product_id}`}
                >
                  <div
                    className="aspect-square overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/product/${item.product_id}`)}
                  >
                    <img
                      src={item.product?.images?.[0] || 'https://images.unsplash.com/photo-1518117823675-9a4bb23df61c'}
                      alt={item.product?.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-3 sm:p-6">
                    <div className="hidden sm:flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} size={14} className="fill-accent text-accent" />
                      ))}
                    </div>
                    <h3
                      className="font-semibold text-sm sm:text-lg mb-1 sm:mb-2 line-clamp-2 cursor-pointer hover:text-accent"
                      onClick={() => navigate(`/product/${item.product_id}`)}
                    >
                      {item.product?.name}
                    </h3>
                    <p className="text-base sm:text-2xl font-bold font-mono mb-2 sm:mb-4">
                      KES {item.product?.price?.toLocaleString()}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="btn-primary flex-1 px-2 sm:px-4 text-xs sm:text-sm"
                        onClick={() => handleMoveToCart(item.product_id)}
                      >
                        <ShoppingCart size={14} className="sm:mr-2" />
                        <span className="hidden sm:inline">Move to Cart</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveFromWishlist(item.product_id)}
                        className="text-red-500 hover:bg-red-50 shrink-0 px-2 sm:px-3"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default WishlistPage;